/**
 * Admin Panel page for global administration
 */
import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import { MainLayout } from '@/components/Layout/MainLayout';
import { AdminCompaniesTab } from '@/components/Admin/AdminCompaniesTab';
import { AdminUsersTab } from '@/components/Admin/AdminUsersTab';
import { AdminAppTab } from '@/components/Admin/AdminAppTab';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { Navigate, useLocation } from 'react-router-dom';

import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const adminTabLabels = ['Companies', 'Users', 'App'] as const;
  useRegisterPageHelp('Administration', adminTabLabels[tabValue] ?? null);

  useEffect(() => {
    console.log('=== AdminPanel Debug ===');
    console.log('Location pathname:', location.pathname);
    console.log('User:', user);
    console.log('User role:', user?.role);
    console.log('User role type:', typeof user?.role);
    console.log('UserRole.ADMIN:', UserRole.ADMIN);
    console.log('UserRole.ORGANIZATION_ADMIN:', UserRole.ORGANIZATION_ADMIN);
    console.log('Is admin check:', user?.role === UserRole.ADMIN || user?.role === 'admin');
    console.log('Is org admin check:', user?.role === UserRole.ORGANIZATION_ADMIN || user?.role === 'organization_admin');
    console.log('======================');
  }, [location, user]);

  // Wait for user to load
  if (!user) {
    console.log('No user yet, waiting...');
    return <Box>Loading...</Box>;
  }

  const isAdmin = user.role === UserRole.ADMIN || user.role === 'admin';
  const isOrgAdmin = user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin';

  // Redirect if not admin
  if (!isAdmin) {
    console.error('User is not admin! Role:', user.role, 'Type:', typeof user.role);
    // If user is organization admin, redirect to organization admin panel
    if (isOrgAdmin) {
      console.log('Redirecting organization admin to /organization-admin');
      return <Navigate to="/organization-admin" replace />;
    }
    // Otherwise redirect to history
    console.log('Redirecting non-admin user to /app/history');
    return <Navigate to="/app/history" replace />;
  }

  console.log('✓ AdminPanel rendered successfully for admin user');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <MainLayout>
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
            <Tab label="Companies" />
            <Tab label="Users" />
            <Tab label="App" />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <AdminCompaniesTab />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AdminUsersTab />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <AdminAppTab />
        </TabPanel>
      </Box>
    </MainLayout>
  );
};
